import { FC } from 'react';
import { styled, SvgIcon, SvgIconProps, ThemeProvider } from '@mui/material';

import { passengerThemeV2 } from '@mopla/mui';
import { getShouldForwardPropFn } from '@mopla/utils';

import LocationPointV2Icon from '../../../icons/LocationPointV2.svg';
import LocationStopV2Icon from '../../../icons/LocationStopV2.svg';
import { RailwayPointIcon } from '../../../icons/RailwayPoint';
import { VirtualStop } from '../../../icons/VirtualStop';
import { TRouteInputValue } from '../types';

const DotIcon = styled('div')(() => ({
	width: 16,
	height: 16,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,

	'&::after': {
		content: '""',
		boxSizing: 'border-box',
		width: 8,
		height: 8,
		borderRadius: '50%',
	},
}));

const NoLocationDotIcon = styled(DotIcon)(({ theme }) => ({
	'&::after': {
		border: `1px solid ${theme.palette.solutions?.[600]}`,
	},
}));

const CurrentLocationDotIcon = styled(DotIcon, {
	shouldForwardProp: getShouldForwardPropFn('pulsating'),
})<{ pulsating?: boolean }>(({ theme, pulsating }) => ({
	'&::after': {
		backgroundColor: theme.palette.myway?.main,
		...(pulsating && {
			animation: 'geoPositionPulse 2s ease-in-out infinite',
		}),
	},
	'@keyframes geoPositionPulse': {
		'0%, 100%': { transform: 'scale(1)', opacity: 1 },
		'50%': { transform: 'scale(1.4)', opacity: 0.7 },
	},
}));

const GeoLoadingDotIcon = styled(DotIcon)(({ theme }) => ({
	'&::after': {
		border: `2px solid ${theme.palette.myway?.main}`,
	},
}));

export interface IPointIconProps {
	/** Location object to determine which icon to display */
	location?: TRouteInputValue;
	/** Icon size */
	size?: 'small' | 'medium' | 'large';
	/** Show empty dot when no location */
	showEmptyDot?: boolean;
	/** Pulsate the current location dot (e.g. when watching position) */
	pulsating?: boolean;
	/** Show blue outline dot while geo position is loading */
	isGeoInitialLoading?: boolean;
	/** Additional SvgIcon props */
	svgIconProps?: Partial<SvgIconProps>;
}

const NonThemedPointIcon: FC<IPointIconProps> = ({
	location,
	size = 'medium',
	showEmptyDot = true,
	pulsating = false,
	isGeoInitialLoading = false,
	svgIconProps,
}) => {
	/** No location yet, but geo is loading — show blue outline dot */
	if (!location && isGeoInitialLoading) {
		return <GeoLoadingDotIcon />;
	}

	/** No location selected */
	if (!location) {
		if (!showEmptyDot) return null;
		return <NoLocationDotIcon />;
	}

	/** Current location (special blue circle) */
	if (location.isCurrentLocation) {
		return <CurrentLocationDotIcon pulsating={pulsating} />;
	}

	/** Railway station */
	if (location.type === 'railway') {
		return (
			<SvgIcon
				component={RailwayPointIcon}
				inheritViewBox
				color="primary"
				fontSize={size}
				{...svgIconProps}
			/>
		);
	}

	/** Virtual stop */
	if (location.type === 'stop' && location.resultType === 'VIRTUAL') {
		return (
			<SvgIcon
				component={VirtualStop}
				inheritViewBox
				color="primary"
				fontSize={size}
				{...svgIconProps}
			/>
		);
	}

	/** Physical stop */
	if (location.type === 'stop') {
		return (
			<SvgIcon
				component={LocationStopV2Icon}
				inheritViewBox
				color="primary"
				fontSize={size}
				{...svgIconProps}
			/>
		);
	}

	/** Address (default) */
	return (
		<SvgIcon
			component={LocationPointV2Icon}
			inheritViewBox
			color="primary"
			fontSize={size}
			{...svgIconProps}
		/>
	);
};

export const PointIcon: FC<IPointIconProps> = (props) => (
	<ThemeProvider theme={passengerThemeV2}>
		<NonThemedPointIcon {...props} />
	</ThemeProvider>
);
